import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaExclamationCircle,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";
import { QUERY_SYSTEM_SETTINGS } from "../../utils/graphql/systemSettings/queries";
import "./MatchProfileCard.css";

/**
 * MatchProfileCard
 * Displays match engine readiness score, profile completeness, and scoring factors breakdown
 *
 * Props:
 * - user: User object with attributes { key, value }
 * - attributeDefs: Array of UserAttributeDefinition objects with { key, label, userEditable, ... }
 * - stats: myJobStats object { assignedCount, totalMeetingsHosted, ... }
 * - onEditProfile: Callback when "Edit Profile" is clicked
 */
export function MatchProfileCard({ user = {}, attributeDefs = [], stats = {}, onEditProfile }) {
  const { data: settingsData } = useQuery(QUERY_SYSTEM_SETTINGS);

  const isMissingValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    return false;
  };

  // Calculate profile completeness
  const { completenessPercent, unfilledDefs } = useMemo(() => {
    const userAttrMap = new Map((user.attributes || []).map((a) => [a.key, a.value]));
    const editableDefs = attributeDefs.filter((def) => def.userEditable && def.active !== false);

    if (editableDefs.length === 0) {
      return { completenessPercent: 100, unfilledDefs: [] };
    }

    const filledCount = editableDefs.filter((def) => !isMissingValue(userAttrMap.get(def.key))).length;

    const unfilled = editableDefs.filter((def) => isMissingValue(userAttrMap.get(def.key)));

    const percent = Math.round((filledCount / editableDefs.length) * 100);
    return { completenessPercent: percent, unfilledDefs: unfilled };
  }, [user.attributes, attributeDefs]);

  // Determine workload level based on meetings hosted
  const getWorkloadLevel = (meetingCount) => {
    if (meetingCount < 10) return { level: "Low", color: "success" };
    if (meetingCount < 30) return { level: "Medium", color: "warning" };
    return { level: "High", color: "danger" };
  };

  const workloadInfo = getWorkloadLevel(stats.totalMeetingsHosted || 0);

  const scoringWeights = useMemo(() => {
    const settings = settingsData?.systemSettings || [];
    const byKey = settings.reduce((acc, setting) => {
      acc[setting.key] = Number(setting.value);
      return acc;
    }, {});

    const rawWeights = {
      constraint: Number.isFinite(byKey.matchEngineConstraintWeight) ? byKey.matchEngineConstraintWeight : 0.4,
      meetingsHosted: Number.isFinite(byKey.matchEngineMeetingsHostedWeight) ? byKey.matchEngineMeetingsHostedWeight : 0.3,
      recentSubs: Number.isFinite(byKey.matchEngineRecentSubsWeight) ? byKey.matchEngineRecentSubsWeight : 0.2,
      totalSubs: Number.isFinite(byKey.matchEngineTotalSubsWeight) ? byKey.matchEngineTotalSubsWeight : 0,
      unassignedApplications: Number.isFinite(byKey.matchEngineUnassignedApplicationsWeight) ? byKey.matchEngineUnassignedApplicationsWeight : 0,
      applicationTiming: Number.isFinite(byKey.matchEngineApplicationTimingWeight) ? byKey.matchEngineApplicationTimingWeight : 0.1,
    };

    const rawTotal = Object.values(rawWeights).reduce((sum, value) => sum + value, 0);
    if (rawTotal <= 0) return rawWeights;

    return Object.fromEntries(
      Object.entries(rawWeights).map(([key, value]) => [key, value / rawTotal])
    );
  }, [settingsData]);

  const formatWeight = (weight) => `${Math.round((weight || 0) * 100)}%`;

  const totalSubJobs = Number.isFinite(stats?.totalSubJobs)
    ? stats.totalSubJobs
    : (Number.isFinite(stats?.assignedCount) ? stats.assignedCount : 0);
  const totalUnassignedApplications = Number.isFinite(stats?.totalUnassignedApplications)
    ? stats.totalUnassignedApplications
    : Math.max(0, (stats?.appliedCount || 0) - (stats?.assignedCount || 0));

  const factorRows = useMemo(() => {
    return [
      {
        key: "constraint",
        title: "Constraint Matching",
        description:
          "Your match attributes determine if you qualify for specific jobs. The more attributes you fill in, the more opportunities you can be matched with.",
        badge: { text: `${completenessPercent}%`, className: "bg-primary" },
      },
      {
        key: "meetingsHosted",
        title: "Workload Balance",
        description: `Substitutes with fewer recent meetings are prioritized to balance workload fairly. Current load: ${workloadInfo.level} (${stats.totalMeetingsHosted || 0} meetings hosted).`,
        badge: { text: `${stats.totalMeetingsHosted || 0} meetings`, className: `bg-${workloadInfo.color}` },
      },
      {
        key: "recentSubs",
        title: "Recent Assignment Load",
        description:
          "Substitutes with fewer recent assignments are prioritized for new opportunities.",
        badge: { text: `${stats.assignedCount || 0} assignments`, className: "bg-secondary" },
      },
      {
        key: "totalSubs",
        title: "Total Assignment History",
        description: `Historical assignment volume used for longer-term workload balancing. Current history: ${totalSubJobs} assignment${totalSubJobs === 1 ? "" : "s"}.`,
        badge: { text: `${totalSubJobs} assignments`, className: "bg-secondary" },
      },
      {
        key: "unassignedApplications",
        title: "Unassigned Application History",
        description: `Applications that did not result in assignment can be weighted for fairness. Current total: ${totalUnassignedApplications}.`,
        badge: { text: `${totalUnassignedApplications} unassigned`, className: "bg-secondary" },
      },
      {
        key: "applicationTiming",
        title: "Application Timing",
        description:
          "Applications submitted closer to posting time can receive higher priority based on timing settings.",
        icon: <FaClock className="text-warning" />,
      },
    ];
  }, [
    completenessPercent,
    workloadInfo.level,
    workloadInfo.color,
    stats.totalMeetingsHosted,
    stats.assignedCount,
    totalSubJobs,
    totalUnassignedApplications,
  ]);

  const [showFactors, setShowFactors] = useState(false);

  return (
    <div className="match-profile-card mt-4">
      {/* Match Readiness Score */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light border-0 py-3">
          <h5 className="mb-0 d-flex align-items-center">
            <FaCheckCircle className="me-2 text-primary" />
            Match Readiness Score
          </h5>
        </div>

        <div className="card-body">
          <div className="match-readiness-container mb-4">
            <div className="readiness-score">
              <div className="readiness-percent">{completenessPercent}%</div>
              <div className="readiness-label">Complete</div>
            </div>

            <div className="progress flex-grow-1 ms-3" style={{ minHeight: "40px" }}>
              <div
                className="progress-bar bg-primary"
                role="progressbar"
                style={{ width: `${completenessPercent}%` }}
                aria-valuenow={completenessPercent}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
          </div>

          <small className="text-muted">
            This score reflects how many of your match attributes you've filled in. A complete
            profile helps admins match you with the right opportunities.
          </small>

          {unfilledDefs.length > 0 && (
            <div className="mt-3 p-3 bg-light rounded">
              <div className="fw-semibold mb-2 text-warning">
                <FaExclamationCircle className="me-2" />
                Missing attributes ({unfilledDefs.length}):
              </div>
              <ul className="mb-0 ps-4">
                {unfilledDefs.slice(0, 5).map((def) => (
                  <li key={def.key} className="text-muted small">
                    {def.label || def.key}
                  </li>
                ))}
                {unfilledDefs.length > 5 && (
                  <li className="text-muted small fw-semibold">
                    ...and {unfilledDefs.length - 5} more
                  </li>
                )}
              </ul>
              <button
                className="btn btn-sm btn-outline-primary mt-2"
                onClick={onEditProfile}
              >
                Edit Profile
              </button>
            </div>
          )}

          {/* Scoring Factors — collapsible inside this card */}
          <div className="mt-3 pt-3 border-top">
            <button
              className="w-100 text-start d-flex align-items-center justify-content-between py-1 match-factors-toggle"
              onClick={() => setShowFactors((v) => !v)}
              aria-expanded={showFactors}
            >
              <span className="d-flex align-items-center">
                <FaClipboardList className="me-2 text-info match-factors-toggle-icon" />
                <span className="fw-semibold match-factors-toggle-label">Match Engine Scoring Factors</span>
              </span>
              {showFactors ? <FaChevronDown className="text-muted match-factors-toggle-chevron" /> : <FaChevronRight className="text-muted match-factors-toggle-chevron" />}
            </button>

            {showFactors && (
              <div className="mt-3">
              <small className="text-muted d-block mb-3">
                The match engine considers these factors when ranking you for available jobs.
              </small>

          {factorRows.map((factor, index) => (
            <div
              key={factor.key}
              className={`factor-row ${index < factorRows.length - 1 ? "mb-3 pb-3 border-bottom" : ""}`}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div className="fw-semibold">{factor.title}</div>
                  <small className="text-muted">Weight: {formatWeight(scoringWeights[factor.key])}</small>
                </div>
                {factor.badge ? (
                  <div className={`badge ${factor.badge.className}`}>{factor.badge.text}</div>
                ) : (
                  factor.icon || null
                )}
              </div>
              <small className="text-muted">{factor.description}</small>
            </div>
          ))}
            </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

export default MatchProfileCard;
